import { Link, useRouter } from 'expo-router';
import { useState } from 'react';
import {
  ImageBackground,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/contexts/AuthContext';
import { validateEmail } from '@/lib/validation';

const wallpaper = require('../../../assets/wallpaper.jpg');

const GOLD = '#C9A843';
const CARD_BG = 'rgba(13, 27, 42, 0.84)';
const CARD_BORDER = 'rgba(201, 168, 67, 0.22)';
const TEXT_WHITE = '#FFFFFF';
const TEXT_MUTED = 'rgba(255,255,255,0.58)';

export default function ForgotPasswordScreen() {
  const { t } = useTranslation();
  const { requestPasswordReset } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit() {
    setFormError(null);
    const err = validateEmail(email);
    if (err) {
      setEmailError(err);
      return;
    }
    setEmailError(undefined);

    setLoading(true);
    const { error } = await requestPasswordReset(email);
    setLoading(false);

    if (error) {
      setFormError(error);
      return;
    }
    router.push({ pathname: '/(auth)/reset-password', params: { email: email.trim() } });
  }

  return (
    <ImageBackground source={wallpaper} style={styles.flex} resizeMode="cover">
      <View style={styles.overlay} />
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={[
            styles.content,
            { paddingTop: insets.top + spacing.xxl, paddingBottom: insets.bottom + spacing.lg },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.card}>
            <View style={styles.header}>
              <Text style={styles.brand}>{t('auth.forgot_password.title')}</Text>
              <Text style={styles.subtitle}>
                {t('auth.forgot_password.subtitle')}
              </Text>
            </View>

            <View style={styles.form}>
              <Input
                label="Email"
                value={email}
                onChangeText={setEmail}
                error={emailError}
                placeholder="name@example.com"
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                textContentType="emailAddress"
              />

              {!!formError && <Text style={styles.formError}>{formError}</Text>}

              <Button title={t('auth.forgot_password.submit')} onPress={onSubmit} loading={loading} />
            </View>

            <View style={styles.footer}>
              <Text style={styles.footerText}>{t('auth.forgot_password.remembered')} </Text>
              <Link href="/(auth)/login" style={styles.footerLink}>
                {t('auth.forgot_password.back')}
              </Link>
            </View>

            <Pressable onPress={() => router.push('/(auth)/reset-password')} hitSlop={8}>
              <Text style={styles.haveCode}>{t('auth.forgot_password.have_code')}</Text>
            </Pressable>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(13, 27, 42, 0.68)',
  },
  content: {
    // Σε μεγάλες οθόνες η φόρμα μένει κεντραρισμένη σε αναγνώσιμο πλάτος
    // αντί να τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
    width: '100%',
    maxWidth: contentMaxWidth.form,
    alignSelf: 'center',
    flexGrow: 1,
    paddingHorizontal: spacing.lg,
    gap: spacing.xl,
    justifyContent: 'center',
  },
  card: {
    backgroundColor: CARD_BG,
    borderRadius: radius.xl,
    borderWidth: 1,
    borderColor: CARD_BORDER,
    padding: spacing.lg,
    gap: spacing.lg,
  },
  header: {
    gap: spacing.sm,
  },
  brand: {
    color: TEXT_WHITE,
    fontSize: fontSize.xl,
    fontWeight: '700',
  },
  subtitle: {
    color: TEXT_MUTED,
    fontSize: fontSize.md,
    lineHeight: 22,
  },
  form: {
    gap: spacing.md,
  },
  formError: {
    color: '#F87171',
    fontSize: fontSize.sm,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerText: {
    color: TEXT_MUTED,
    fontSize: fontSize.md,
  },
  footerLink: {
    color: GOLD,
    fontSize: fontSize.md,
    fontWeight: '600',
  },
  haveCode: {
    color: TEXT_MUTED,
    fontSize: fontSize.sm,
    textAlign: 'center',
    textDecorationLine: 'underline',
  },
});
